<template>
    <div v-if="loading||(loaded&&!!totalRecords)" class="nibnut-data-table">
        <div v-if="cardTitle||filterable||has_additional_buttons||show_search" class="card-header">
            <div v-if="!!cardTitle" class="card-title h5 mb-4">{{ cardTitle }}</div>
            <div v-if="filterable||has_additional_buttons||show_search" class="columns">
                <div v-if="filterable" class="column col-sm-12 mb-2">
                    <slot name="filters"></slot>
                </div>
                <div v-if="has_additional_buttons" :class="{'text-right': !tight_fit}" class="column col-sm-6 mb-2">
                    <slot name="additional_buttons"></slot>
                </div>
                <div v-if="show_search" class="column col-sm-12 mb-2">
                    <slot name="search">
                        <div class="has-icon-left">
                            <input :class="{'input-sm': compact}" class="form-input" type="search" :placeholder="searchPlaceholder" v-model="query" @focus="autoselect">
                            <open-icon glyph="search" class="form-icon"></open-icon>
                        </div>
                    </slot>
                </div>
            </div>
        </div>
        <table class="table">
            <thead v-if="!noThead">
                <tr>
                    <th v-for="(meta, field) in headers" :key="field" :colspan="header_span(field)" :class="meta.head_class">
                        <data-table-header-cell :field="field" :meta="meta" @click="sort">
                            <template v-if="has_header_content" slot="cell" slot-scope="props">
                                <slot name="thead" :field="props.field" :meta="props.meta"></slot>
                            </template>
                        </data-table-header-cell>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td :colspan="nb_columns" class="text-center">
                        <div class="loading loading-lg"></div>
                    </td>
                </tr>
                <tr v-else-if="!found">
                    <td :colspan="nb_columns" class="text-center">
                        <em>{{ $root.translate("No results found...") }}</em>
                    </td>
                </tr>
                <tr v-else v-for="row in rows" :key="row.id" :class="{'active': row_selected(row)}" @click="row_click(...arguments, row)" class="nibnut-hoverable">
                    <td v-for="(meta, field) in columns" :key="field" :class="meta.cell_class||{}">
                        <slot name="tbody" :row="row" :field="field" :meta="meta">
                            {{ row[field] }}
                        </slot>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="(!noTfoot||canAdd)&&!loading&&!!found">
                <slot name="addl-tfoots"></slot>
                <tr v-if="has_footer_content">
                    <td v-for="(meta, field) in columns" :key="field" :class="meta.head_class">
                        <slot name="tfoot" :field="field" :meta="meta">
                            {{ field }}
                        </slot>
                    </td>
                </tr>
                <tr v-if="canAdd">
                    <td :colspan="nb_columns" class="text-center nibnut-data-table-footer-add">
                        <button type="button" class="btn btn-link btn-block btn-sm" @click.prevent="add">
                            <open-icon glyph="plus" class="mr-2"></open-icon>
                            {{ add_button_title }}
                        </button>
                    </td>
                </tr>
            </tfoot>
        </table>
        <div v-if="!loading&&((totalPages>1)||showTotals)" class="text-center">
            <div class="btn-group my-4">
                <button
                    :class="{'disabled': currentPage===1}"
                    class="btn"
                    @click.prevent="paginate_to(1)"
                >
                    {{ $root.translate("First") }}
                </button>
                <button
                    v-if="currentPage > 1"
                    class="btn"
                    @click.prevent="paginate_to(currentPage - 1)"
                >
                    {{ $root.translate("Previous") }}
                </button>
                <button
                    v-for="page in pages"
                    :key="page.number"
                    :class="{'active': page.number===currentPage}"
                    class="btn"
                    @click.prevent="paginate_to(page.number)"
                >
                    {{ page.number }}
                </button>
                <button
                    v-if="currentPage < totalPages"
                    class="btn"
                    @click.prevent="paginate_to(currentPage + 1)"
                >
                    {{ $root.translate("Next") }}
                </button>
                <button
                    :class="{'disabled': currentPage===totalPages}"
                    class="btn"
                    @click.prevent="paginate_to(totalPages)"
                >
                    {{ $root.translate("Last") }}
                </button>
            </div>
            <div v-if="showTotals" :class="{'mt-4': (totalPages<=1)}" class="text-small text-gray text-center">
                <span v-if="found!==totalRecords">{{ found_caption }} / </span>{{ total_caption }}
            </div>
        </div>
    </div>
    <div v-else-if="!loading&&loaded&&!totalRecords" class="empty">
        <slot name="empty">
            <p class="empty-title h5">{{ empty_title }}</p>
            <div v-if="!!canAdd" class="empty-action">
                <button type="button" class="btn btn-primary" @click.prevent="add">
                    <open-icon glyph="plus" class="mr-2"></open-icon>
                    {{ empty_add_button_title }}
                </button>
            </div>
        </slot>
    </div>
    <div v-else>{{loading}}, {{loaded}}, {{totalRecords}}</div>
</template>

<script type="text/javascript">
import { debounce, forOwn } from "lodash"

import DataTableHeaderCell from "./DataTableHeaderCell"
import { OpenIcon } from "../../nibnut/components"

export default {
    name: "DataTable",
    watch: {
        search: "update_query",
        query: "debounce_query"
    },
    components: {
        DataTableHeaderCell,
        OpenIcon
    },
    methods: {
        autoselect (event) {
            event.target.select()
        },
        update_query () {
            if(this.query !== this.search) this.query = this.search
        },
        debounce_query: debounce(function () {
            this.$emit("search", this.query)
        }, 500),
        sort (field, meta) {
            this.$emit("sort", field, meta)
        },
        header_span (field) {
            return 1
        },
        row_selected (row) {
            if(!this.selectable) return false
            return this.selection.indexOf(row[this.idField]) >= 0
        },
        row_toggle_selection (row) {
            if(!this.selectable) return
            const selected = this.row_selected(row)
            if(selected && this.unselectable) this.selection.splice(this.selection.indexOf(row[this.idField]), 1)
            if(!selected) {
                if(!this.unselectable && !!this.selection.length) this.selection = []
                this.selection.push(row[this.idField])
            }
        },
        row_click (event, row) {
            /*
            let is_button = (!!event.target && !!event.target.classList && event.target.classList.contains("btn"))
            if(!is_button) is_button = (!!event.target.parentNode && !!event.target.parentNode.classList && event.target.parentNode.classList.contains("btn"))
            if(is_button) return
            event.preventDefault()
            */
            this.$emit("click", row, this.row_selected(row), event)
            this.row_toggle_selection(row)
        },
        paginate_to (page) {
            this.$emit("page", page)
        },
        add () {
            this.$emit("add")
        }
    },
    computed: {
        is_card () {
            return (this.cardTitle !== null)
        },
        show_search () {
            if(this.searchConditional) return (this.search !== null) && (this.totalRecords > this.perPage)
            return (this.search !== null)
        },
        columns () {
            const columns = {}
            if(!this.collapsed) {
                forOwn(this.fields, (meta, field) => {
                    if(!meta.condition || !!meta.condition()) columns[field] = meta
                })
            } else {
                columns._summary = { label: (this.collapsed === true) ? " " : this.collapsed, sort: false }
                forOwn(this.fields, (meta, field) => {
                    if(meta.uncollapsable) columns[field] = meta
                })
            }
            return columns
        },
        nb_columns () {
            return Object.keys(this.columns).length
        },
        headers () {
            const headers = {}
            forOwn(this.columns, (meta, field) => {
                if((this.collapsed !== true) || meta.uncollapsable) {
                    if(!meta.condition || !!meta.condition()) headers[field] = meta
                }
            })
            return headers
        },
        filterable () {
            return !!this.$slots && !!this.$slots.filters
        },
        has_header_content () {
            return !!this.$scopedSlots && !!this.$scopedSlots.thead
        },
        has_additional_buttons () {
            return !!this.$slots && !!this.$slots.additional_buttons
        },
        has_footer_content () {
            return !this.noTfoot && !!this.$scopedSlots && !!this.$scopedSlots.tfoot
        },
        pages () {
            const pages = []

            const visible_around = Math.ceil((this.pageRange - 1) / 2)

            let from = this.currentPage - visible_around
            if(from <= 0) from = 1

            let to = from + (this.pageRange - 1)
            if(to > this.totalPages) {
                from -= (to - this.totalPages)
                if(from <= 0) from = 1
                to = this.totalPages
            }

            // if((from - 1) >= 1) pages.push({number: null, delta: -1})

            for(let loop = from; loop <= to; loop++) pages.push({ number: loop })

            // if((this.totalPages - to) >= 1) pages.push({number: null, delta: 1})

            return pages
        },
        has_prev_pages () {
            return this.currentPage > 1
        },
        has_next_pages () {
            return this.currentPage < this.totalPages
        },
        tight_fit () {
            return this.$mq.match(/^(xs|sm)$/i)
        },
        found_caption () {
            return this.$root.translate("{number} {singular_entity_name} found:::{number} {plural_entity_name} found", { number: this.found, singular_entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase(), plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() }, this.found)
        },
        total_caption () {
            return this.$root.translate("{number} {singular_entity_name}:::{number} {plural_entity_name}", { number: this.totalRecords, singular_entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase(), plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() }, this.totalRecords)
        },
        empty_title () {
            return this.$root.translate("No {plural_entity_name} set up yet", { plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() })
        },
        empty_add_button_title () {
            return this.$root.translate("Add your first {entity_name}...", { entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase() })
        },
        add_button_title () {
            return this.addButtonTitle || this.$root.translate("Add a new {entity_name}...", { entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase() })
        }
    },
    props: {
        fields: {
            type: Object,
            required: true
        },
        rows: {
            type: Array,
            required: true
        },
        entityName: {
            type: String,
            default: "record:::records"
        },
        idField: {
            type: String,
            default: "id"
        },
        found: {
            type: Number,
            default: 0
        },
        totalRecords: {
            type: Number,
            default: 0
        },
        showTotals: {
            type: Boolean,
            default: true
        },
        loaded: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        selectable: {
            type: Boolean,
            default: false
        },
        unselectable: {
            type: Boolean,
            default: true
        },
        search: {
            type: String,
            default: "" // null is not searchable, "" or "..." is searchable and starts with that search value
        },
        searchPlaceholder: {
            type: String,
            default: "Search..."
        },
        searchConditional: {
            type: Boolean,
            default: false
        },
        currentPage: {
            type: Number,
            default: 1
        },
        perPage: {
            type: Number,
            default: 10
        },
        totalPages: {
            type: Number,
            default: 1
        },
        pageRange: {
            type: Number,
            default: 6
        },
        canAdd: {
            type: Boolean,
            default: false
        },
        addButtonTitle: {
            type: String,
            default: ""
        },
        cardTitle: { // null for regular table, anything else to display as card
            validator: prop => {
                return (typeof prop === "string") || (prop === null)
            },
            default: null
        },
        collapsed: {
            validator: prop => {
                return (typeof prop === "string") || (typeof prop === "boolean")
            },
            default: false // title of the summary column
        },
        compact: {
            type: Boolean,
            default: false
        },
        noThead: {
            type: Boolean,
            default: false
        },
        noTfoot: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            query: this.search || "", // local version; "search" prop is parent's version, and the one that triggers actual search
            selection: []
        }
    }
}
</script>
